/**
 * MathText — renders text containing LaTeX math notation.
 *
 * Delimiters:
 *   $$...$$  → display math (centered, block)
 *   $...$    → inline math
 *
 * Plain text passes through unchanged. Existing questions without $
 * delimiters render normally — zero breaking changes.
 *
 * RTL note (Arabic expansion, W1 + Chunk 32): every rendered math segment is
 * wrapped with dir="ltr" and unicode-bidi isolation, and the OUTER wrapper
 * uses unicode-bidi: plaintext — the span derives its paragraph direction
 * from its own first strong character. Arabic content therefore lays out
 * right-to-left even inside an LTR page or a container whose dir was lost,
 * and English content stays LTR inside RTL pages. This made the old global
 * rule `[dir="rtl"] .math-text { direction:ltr; unicode-bidi:embed }`
 * obsolete AND harmful (it flipped whole Arabic sentences); it is removed
 * from global.css in the same chunk. Math notation is inherently
 * left-to-right; without isolation, the Unicode bidi algorithm reorders
 * digits and operators inside RTL paragraphs (observed live: "$8x + 8 =
 * 160$" displayed as "88x + 160 =" in Arabic question views). Isolation
 * has no effect in LTR (English) contexts.
 *
 * Requires: npm install katex
 * KaTeX CSS is imported here so it's only loaded when this component is used.
 */

import React, { useMemo } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

// Applied to every math wrapper span. dir="ltr" alone triggers isolation in
// modern browsers (UA stylesheet applies unicode-bidi: isolate to [dir]);
// the explicit style is belt-and-braces for older engines.
const LTR_ISOLATE = 'direction:ltr;unicode-bidi:isolate;';

// ── Chunk 47: long text inside math must be able to WRAP ────────────────────
// Some questions were authored with the whole sentence inside math ($\text{…}$
// or bare $…$). KaTeX lays out `.base` as white-space:nowrap / width:min-content,
// so such a sentence runs past the card edge. Two defences:
//   1. unwrapText(): a segment that is ENTIRELY \text{…} is rendered as plain
//      prose (what the author meant) — proper font, proper wrapping, RTL-safe.
//   2. the style below lets any remaining KaTeX output wrap at the spaces
//      inside text-mode runs. Math-mode spacing is mspace, not characters, so
//      real formulas gain no break opportunities and are unaffected.
const WRAP_STYLE_ID = 'math-text-wrap-style';
if (typeof document !== 'undefined' && !document.getElementById(WRAP_STYLE_ID)) {
  const el = document.createElement('style');
  el.id = WRAP_STYLE_ID;
  el.textContent = '.math-text .katex .base{white-space:normal;width:auto}.math-text .katex{white-space:normal}';
  document.head.appendChild(el);
}

/**
 * If `latex` is exactly one \text{...} (balanced braces, nothing else),
 * return the inner text; otherwise null. Nested \text inside is flattened.
 */
function unwrapText(latex) {
  const src = latex.trim();
  if (!src.startsWith('\\text{') || !src.endsWith('}')) return null;
  let depth = 0;
  for (let i = 5; i < src.length; i++) {          // index 5 = the opening brace
    if (src[i] === '{') depth++;
    else if (src[i] === '}') { depth--; if (depth === 0 && i !== src.length - 1) return null; }
  }
  if (depth !== 0) return null;
  return src.slice(6, -1).replace(/\\text\{([^{}]*)\}/g, '$1');
}

/**
 * Parse text into segments of plain text and LaTeX.
 * Handles $$...$$ (display) first, then $...$ (inline).
 */
function parseSegments(text) {
  if (!text || typeof text !== 'string') return [{ type: 'text', value: text || '' }];

  const segments = [];
  // Split on display math first: $$...$$
  const displayParts = text.split(/(\$\$[\s\S]+?\$\$)/g);

  for (const part of displayParts) {
    if (part.startsWith('$$') && part.endsWith('$$')) {
      segments.push({ type: 'display', value: part.slice(2, -2).trim() });
    } else {
      // Now split remaining on inline math: $...$
      const inlineParts = part.split(/(\$[^$\n]+?\$)/g);
      for (const ip of inlineParts) {
        if (ip.startsWith('$') && ip.endsWith('$') && ip.length > 2) {
          const inner = ip.slice(1, -1).trim();
          const plain = unwrapText(inner);                       // Chunk 47
          segments.push(plain !== null ? { type: 'text', value: plain } : { type: 'inline', value: inner });
        } else if (ip) {
          segments.push({ type: 'text', value: ip });
        }
      }
    }
  }

  return segments;
}

/**
 * Render a single LaTeX string to HTML using KaTeX.
 * Returns raw HTML string. Falls back to showing the LaTeX source on error.
 */
function renderLatex(latex, displayMode = false) {
  try {
    return katex.renderToString(latex, {
      displayMode,
      throwOnError: false,
      strict: false,
      trust: true,
      // Common macros for Saudi math exams
      macros: {
        '\\R': '\\mathbb{R}',
        '\\N': '\\mathbb{N}',
        '\\Z': '\\mathbb{Z}',
        '\\Q': '\\mathbb{Q}',
      },
    });
  } catch (e) {
    // Fallback: show the raw LaTeX in a styled span (LTR-isolated — LaTeX
    // source is Latin notation and scrambles in RTL context too)
    return `<span class="math-error" dir="ltr" style="${LTR_ISOLATE}" title="LaTeX error: ${e.message}">${latex}</span>`;
  }
}

/**
 * MathText component.
 *
 * Usage:
 *   <MathText text="Find $\frac{1}{2} + \frac{3}{4}$" />
 *   <MathText text="$$x = \frac{-b \pm \sqrt{b^2-4ac}}{2a}$$" />
 *   <MathText text="Plain text with no math" />
 */
export default function MathText({ text, className = '', style = {} }) {
  const rendered = useMemo(() => {
    if (!text) return '';

    // Quick check: if there are no $ characters, skip parsing entirely
    if (!text.includes('$')) {
      return null; // signals to render as plain text
    }

    const segments = parseSegments(text);
    return segments.map((seg, i) => {
      if (seg.type === 'display') {
        return `<span class="math-display" dir="ltr" style="${LTR_ISOLATE}" key="${i}">${renderLatex(seg.value, true)}</span>`;
      }
      if (seg.type === 'inline') {
        return `<span class="math-inline" dir="ltr" style="${LTR_ISOLATE}">${renderLatex(seg.value, false)}</span>`;
      }
      // Plain text — escape HTML
      return seg.value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }).join('');
  }, [text]);

  // No math delimiters found — render as plain text (no dangerouslySetInnerHTML)
  if (rendered === null) {
    return <span className={className} style={{ unicodeBidi: 'plaintext', ...style }}>{text}</span>;
  }

  return (
    <span
      className={`math-text ${className}`}
      style={{ unicodeBidi: 'plaintext', ...style }}
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  );
}

/**
 * LaTeX Cheatsheet data for admin panel tooltip.
 */
export const LATEX_CHEATSHEET = [
  { label: 'Fraction',       code: '\\frac{a}{b}',                  example: '\\frac{1}{2}' },
  { label: 'Square root',    code: '\\sqrt{x}',                     example: '\\sqrt{16}' },
  { label: 'Nth root',       code: '\\sqrt[n]{x}',                  example: '\\sqrt[3]{27}' },
  { label: 'Exponent',       code: 'x^{n}',                         example: 'x^{2}' },
  { label: 'Subscript',      code: 'x_{n}',                         example: 'a_{1}' },
  { label: 'Plus-minus',     code: '\\pm',                          example: '\\pm 5' },
  { label: 'Infinity',       code: '\\infty',                       example: '\\infty' },
  { label: 'Not equal',      code: '\\neq',                         example: 'a \\neq b' },
  { label: 'Less/greater',   code: '\\leq \\geq',                   example: 'x \\leq 10' },
  { label: 'Pi',             code: '\\pi',                          example: '2\\pi r' },
  { label: 'Theta',          code: '\\theta',                       example: '\\sin\\theta' },
  { label: 'Trig functions', code: '\\sin \\cos \\tan',             example: '\\sin(30°)' },
  { label: 'Degree symbol',  code: '^{\\circ}',                     example: '90^{\\circ}' },
  { label: 'Absolute value', code: '|x|',                           example: '|x-3|' },
  { label: 'Vector',         code: '\\vec{v}',                      example: '\\vec{AB}' },
  { label: 'Summation',      code: '\\sum_{i=1}^{n}',              example: '\\sum_{i=1}^{n} i' },
  { label: 'Integral',       code: '\\int_{a}^{b}',                example: '\\int_{0}^{1} x\\,dx' },
  { label: 'Greek letters',  code: '\\alpha \\beta \\gamma \\delta', example: '\\alpha + \\beta' },
  { label: 'Parentheses (big)', code: '\\left( \\right)',           example: '\\left(\\frac{a}{b}\\right)' },
  { label: 'Display block',  code: '$$...$$',                       example: '$$\\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}$$' },
  { label: 'Inline math',    code: '$...$',                         example: 'Find $x$ if $2x+3=7$' },
];